"use client";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { Button } from "@/app/components/shadcn/button";
import { Badge } from "@/app/components/shadcn/badge";
import { Skeleton } from "@/app/components/shadcn/skeleton";
import { Star, Check, Info, Users } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/components/shadcn/tooltip";
import useModal from "@/app/lib/features/modal/modal.hook";
import useStore from "@/app/lib/features/store/store.hook";
import useHasAuthToken from "@/app/hooks/useHasAuthToken";
import { toggleStoreFollow } from "@/app/lib/features/store/store.thunk";
import { StoreSeller } from "@/app/lib/features/store/store.types";
import { trackSellerFollow } from "@/app/lib/tracking/interactions";

interface StoreHeaderProps {
  seller: StoreSeller;
}

const StoreHeader = ({ seller: initialSeller }: StoreHeaderProps) => {
  const { createModal } = useModal();
  const {
    seller: reduxSeller,
    toggleFollow,
    isFollowLoading,
    isSellerError,
  } = useStore();
  const hasAuthToken = useHasAuthToken();

  const seller = reduxSeller ?? initialSeller;
  const isFollowed = seller.is_followed;

  // `is_followed` auth'a bağlı ama sayfa ISR ile anonim üretiliyor — prop olarak
  // gelen initialSeller daima is_followed:false taşır. Giriş yapmış bir takipçide
  // buton "Takip Et" basılıp client fetch dönünce "Takip Ediliyor"a atlıyordu.
  //
  // reduxSeller !== null ⟺ getStoreFullData (token'lı) dönmüş demek: Redux'a
  // seller yalnızca oradan yazılıyor. Status'e değil veriye bakılıyor ki sekme
  // geçişindeki tazeleme fetch'i ("loading") placeholder'ı geri getirmesin.
  // Fetch hata verirse de beklemeyi bitir, yoksa placeholder asılı kalır.
  const isFollowStateKnown = !hasAuthToken || reduxSeller !== null || isSellerError;

  const handleFollowClick = async () => {
    const result = await toggleFollow(seller.id);

    if (toggleStoreFollow.fulfilled.match(result)) {
      if (result.payload.is_following) {
        trackSellerFollow(seller.id);
        createModal("follow-reward", { followData: result.payload });
      }
    }
  };

  return (
    <div className="relative w-full bg-gray-50">
      <div className="relative w-full h-48 md:h-56 lg:h-64">
        <FallbackImage
          src={seller.banner}
          alt={seller.name}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/30 to-transparent" />
      </div>

      <div className="container-main">
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-6 -mt-20 md:-mt-24">
            <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white shadow-xl overflow-hidden bg-white shrink-0">
              <FallbackImage
                src={seller.logo}
                alt={seller.name}
                fill
                className="object-cover"
              />
            </div>

            <div className="flex-1 w-full bg-white rounded-xl shadow-lg p-6 md:mb-4 md:h-40">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 h-full">
                <div className="flex-1 space-y-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {seller.name}
                    </h1>
                    {seller.is_verified && (
                      <Badge className="gap-1.5 bg-blue-50 text-blue-600 border-blue-200 px-3 py-1">
                        <Check size={16} />
                        <span className="font-medium">Doğrulanmış</span>
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <Star
                        size={18}
                        className="fill-yellow-400 text-yellow-400"
                      />
                      <span className="font-bold text-base">
                        {seller.rating}
                      </span>
                    </div>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 transition-colors">
                          <Info size={18} className="text-gray-500" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-sm">
                        <p className="text-sm leading-relaxed">{seller.info}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                <div className="flex flex-col gap-3 shrink-0 lg:items-end justify-center">
                  {isFollowStateKnown ? (
                    <Button
                      onClick={handleFollowClick}
                      size="lg"
                      variant={isFollowed ? "outline" : "default"}
                      disabled={isFollowLoading}
                      className="w-full lg:w-auto min-w-48 font-semibold"
                    >
                      {isFollowed ? (
                        <>
                          <Check size={20} />
                          Takip Ediliyor
                        </>
                      ) : (
                        "Takip Et"
                      )}
                    </Button>
                  ) : (
                    <Skeleton className="h-10 w-full lg:w-48 rounded-md" />
                  )}
                  <div className="flex items-center gap-2 text-gray-600 lg:justify-end">
                    <Users size={18} />
                    <span className="font-medium">
                      {seller.follower_count.toLocaleString("tr-TR")} takipçi
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreHeader;
